import { v4 as uuidv4 } from 'uuid'
import { LocalNotifications } from '@capacitor/local-notifications'
import { AndroidSettings, IOSSettings, NativeSettings } from 'capacitor-native-settings'
import { useAppMode } from '~/composables/app'
import { GrilldPushNotifications } from '~/capacitor-plugins/grilld-push-notifications'

// Declare the SalesForce Marketing Cloud plugin so Typescript is happy
declare let MCCordovaPlugin: any

const PUSH_TOKEN_TIMEOUT_MS = 3000

/**
 * @callback module:MCCordovaPlugin~notificationOpenedCallback
 * @param {number} timeStamp - Time since epoch when the push message was
 *     opened.
 * @param {Object} values - The values of the notification message.
 * @param {string} values.alert - The alert text of the notification
 *     message.
 * @param {string} [values.title] - The title text of the notification
 *     message.
 * @param {string} [values.url] - The url associated with the notification
 *     message. This can be either a cloud-page url or an open-direct url.
 * @param {string} values.type - Indicates the type of notification message.
 *     Possible values: 'cloudPage', 'openDirect' or 'other'
 */
export interface NotificationOpenedCallbackEvent {
  timestamp?: number
  values?: {
    alert?: string
    subtitle?: string
    title?: string
    url?: string
    /**
     * @description The deep link path associated with the notification message.
     */
    path?: string
    type?: 'cloudPage' | 'openDirect' | 'other'
  }
  type?: string
}

/**
 * @description Setup the SalesForce Marketing Cloud plugin for mobile notifications.
 */
export function useSalesforceNotifications() {
  const { isWebMode } = useAppMode()

  async function getSystemToken(): Promise<string> {
    if (isWebMode || !isPlatform('capacitor')) {
      return ''
    }

    // Push is optional: bound the entire lookup, including the permission check.
    return new Promise<string>((resolve) => {
      let settled = false
      const timer = setTimeout(() => fail('timed out'), PUSH_TOKEN_TIMEOUT_MS)

      function finish(token: string) {
        if (settled) return
        settled = true
        clearTimeout(timer)
        resolve(token)
      }

      function fail(reason: string) {
        if (settled) return
        console.warn(`Push token lookup ${reason}; continuing without push notifications`)
        finish('')
      }

      void isPushEnabled()
        .then((enabled) => {
          // A late permission callback must not start another native request.
          if (settled) return
          if (!enabled) {
            finish('')
            return
          }
          MCCordovaPlugin.getSystemToken(
            (token: unknown) => finish(typeof token === 'string' ? token : ''),
            () => fail('failed')
          )
        })
        .catch(() => fail('failed'))
    })
  }

  async function getDeviceId(): Promise<string> {
    if (isWebMode || !isPlatform('capacitor')) {
      return ''
    }
    return await new Promise<string>((resolve, reject) => {
      MCCordovaPlugin.getDeviceId(
        (deviceId: string) => {
          resolve(deviceId)
        },
        () => {
          console.error('MCCordovaPlugin.getDeviceId failed')
          reject(new Error('MCCordovaPlugin.getDeviceId failed'))
        }
      )
    })
  }

  async function logSdkState(): Promise<void> {
    if (isWebMode || !isPlatform('capacitor')) {
      return
    }

    return await new Promise<void>((resolve, reject) => {
      MCCordovaPlugin.logSdkState(
        () => {
          resolve()
        },
        () => {
          console.error('MCCordovaPlugin.logSdkState failed')
          reject(new Error('MCCordovaPlugin.logSdkState failed'))
        }
      )
    })
  }

  async function isPushEnabled(): Promise<boolean> {
    if (isWebMode || !isPlatform('capacitor')) {
      return false
    }
    return await new Promise<boolean>((resolve, reject) => {
      MCCordovaPlugin.isPushEnabled(
        (enabled: boolean) => {
          resolve(enabled)
        },
        () => {
          console.error('MCCordovaPlugin.isPushEnabled failed')
          reject(new Error('MCCordovaPlugin.isPushEnabled failed'))
        }
      )
    })
  }

  async function enablePush(): Promise<void> {
    if (isWebMode || !isPlatform('capacitor')) {
      return
    }
    return await new Promise<void>((resolve, reject) => {
      MCCordovaPlugin.enablePush(
        () => {
          resolve()
        },
        () => {
          console.error('MCCordovaPlugin.enablePush failed')
          reject(new Error('MCCordovaPlugin.enablePush failed'))
        }
      )
    })
  }

  async function enableLogging(): Promise<void> {
    if (isWebMode || !isPlatform('capacitor')) {
      return
    }
    return await new Promise<void>((resolve, reject) => {
      MCCordovaPlugin.enableLogging(
        () => {
          resolve()
        },
        () => {
          console.error('MCCordovaPlugin.enableLogging failed')
          reject(new Error('MCCordovaPlugin.enableLogging failed'))
        }
      )
    })
  }

  async function disableLogging(): Promise<void> {
    if (isWebMode || !isPlatform('capacitor')) {
      return
    }
    return await new Promise<void>((resolve, reject) => {
      MCCordovaPlugin.disableLogging(
        () => {
          resolve()
        },
        () => {
          console.error('MCCordovaPlugin.disableLogging failed')
          reject(new Error('MCCordovaPlugin.disableLogging failed'))
        }
      )
    })
  }

  async function setContactKey(contactKey: string): Promise<void> {
    if (isWebMode || !isPlatform('capacitor')) {
      return
    }
    return await new Promise<void>((resolve, reject) => {
      MCCordovaPlugin.setContactKey(
        contactKey,
        () => {
          resolve()
        },
        () => {
          console.error('mcsdk MCCordovaPlugin.setContactKey failed')
          reject(new Error('MCCordovaPlugin.setContactKey failed'))
        }
      )
    })
  }

  function setOnNotificationOpenedWhenTerminatedListener(callback: (event: { path: string }) => void): void {
    if (isWebMode || !isPlatform('capacitor')) {
      return
    }
    GrilldPushNotifications.addListener('opened', callback)
  }

  function setOnNotificationOpenedListener(callback: (event: NotificationOpenedCallbackEvent) => void): void {
    if (isWebMode || !isPlatform('capacitor')) {
      return
    }
    MCCordovaPlugin.setOnNotificationOpenedListener(callback)
  }

  function setOnUrlActionListener(callback: (url: string) => void): void {
    if (isWebMode || !isPlatform('capacitor')) {
      return
    }
    MCCordovaPlugin.setOnUrlActionListener(callback)
  }
  /**
   * @description Setup the SalesForce Marketing Cloud plugin for mobile notifications.
   * This is for relish users who have a salesforce contact id.
   * @param salesforceContactId {string}
   */
  async function setupMobileNotificationsForRelishUser(salesforceContactId: string): Promise<void> {
    if (isWebMode || !isPlatform('capacitor')) {
      return
    }

    return await setContactKey(salesforceContactId)
  }

  /**
   * @description Setup the SalesForce Marketing Cloud plugin for mobile notifications.
   * This is for guest users who don't have a salesforce contact id.
   */
  async function setupMobileNotificationsForGuestUser(): Promise<void> {
    if (isWebMode || !isPlatform('capacitor')) {
      return
    }

    return await setContactKey(uuidv4())
  }

  return {
    enablePush,
    isPushEnabled,
    setupMobileNotificationsForRelishUser,
    setupMobileNotificationsForGuestUser,
    logSdkState,
    getDeviceId,
    getSystemToken,
    enableLogging,
    disableLogging,
    setOnNotificationOpenedWhenTerminatedListener,
    setOnNotificationOpenedListener,
    setOnUrlActionListener
  }
}

/**
 * @description Provides to setup mobile notifications provided by Capacitor
 */
export function useCapacitorNotifications() {
  async function requestNotificationPermissions(): Promise<PermissionState> {
    const { display } = await LocalNotifications.checkPermissions()

    if (display === 'granted') {
      return display
    } else {
      return (await LocalNotifications.requestPermissions()).display as PermissionState
    }
  }

  async function presentNotificationSettings(): Promise<void> {
    await NativeSettings.open({
      optionAndroid: AndroidSettings.AppNotification,
      optionIOS: IOSSettings.Notifications
    })
  }

  return {
    requestNotificationPermissions,
    presentNotificationSettings
  }
}
